import React, { useState, useEffect, useContext } from "react";
import { CSVLink } from "react-csv";
import RunDraw from "./RunDraw";
import { addDays } from "date-fns";
import restService from "../../../../restService";
import { DatePicker } from "../../../components";
import Table from "./table";
import UserContext from "../../../../contexts/UserContext";
import CampaignContext from "../../../../contexts/CampaignContext";
import moment from "moment";
import { PulseLoader } from "react-spinners";
import { useSearchParams } from "react-router-dom";
import {
  drawWinnersFiltersState,
  drawWinnersState,
  setDrawWinners,
  setDrawWinnersFilters,
} from "../../../../redux/entriesSlice";
import { useSelector, useDispatch } from "react-redux";

const ExportData = ({ transactions, showButtonTitle }) => {
  const [csvData, setCsvData] = useState([]);

  // :::: EXPORT TRANSACTIONS :::::
  useEffect(() => {
    // EXPORTING THE DATA INTO CSV FILE
    const toCSV = [];
    for (let i = 0; i < transactions.length; i++) {
      let obj = {};

      obj["date"] = moment(new Date(transactions[i].date)).format(
        "DD/MM/YYYY, h:mm:ss A"
      );
      obj["phone"] = transactions[i]?.msisdn;

      // Include "Gender" header only if it has data
      if (transactions[i].gender) {
        obj["gender"] = transactions[i]?.gender;
      }

      // Include "Region" header only if it has data
      if (transactions[i].region) {
        obj["region"] = transactions[i]?.region;
      }

      // Include "wins" header only if it has data
      if (transactions[i].wins) {
        obj["wins"] = transactions[i]?.wins;
      }

      // Include "Number of Entries" header only if it has data
      if (transactions[i].entries) {
        obj["entries"] = transactions[i]?.entries;
      }
      // Include "Number of Entries" header only if it has data
      if (transactions[i].name) {
        obj["name"] = transactions[i]?.name;
      }

      toCSV.push(obj);
    }

    setCsvData(toCSV);
  }, [transactions]);

  const reportHeader = [
    { label: "Selection Date", key: "date" },
    { label: "Phone Number", key: "phone" },

    // Include "Gender" header only if it has data
    ...(transactions.some((t) => t.gender)
      ? [{ label: "Gender", key: "gender" }]
      : []),

    // Include "Region" header only if it has data
    ...(transactions.some((t) => t.region)
      ? [{ label: "Region", key: "region" }]
      : []),
    // Include "Wins" header only if it has data
    ...(transactions.some((t) => t.wins)
      ? [{ label: "Number of Wins", key: "wins" }]
      : []),

    // Include "Number of Entries" header only if it has data
    ...(transactions.some((t) => t.entries)
      ? [{ label: "Number of Entries", key: "entries" }]
      : []),
    { label: "Name", key: "name" },
    { label: "Gift", key: "gift" },
  ];

  const csvFile = {
    filename: "transactions.csv",
    headers: reportHeader,
    data: csvData,
  };

  return (
    <div className="md:ml-4">
      <CSVLink
        {...csvFile}
        className="flex items-center justify-center border rounded border-[#3989DD] text-xs md:text-base py-[6px] px-4 transition hover:bg-[#3989DD1a] hover:shadow-lg"
        type="button"
      >
        <span className="px-1 pb-1">
          <svg
            width="16"
            height="16"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_371_37)">
              <path
                d="M6 0.5625V7.875"
                stroke="currentColor"
                strokeMiterlimit="10"
              />
              <path
                d="M4.125 2.4375L6 0.5625L7.875 2.4375"
                stroke="currentColor"
                strokeMiterlimit="10"
                strokeMiterLimit="square"
              />
              <path
                d="M7.5 4.875H9.375C9.62364 4.875 9.8621 4.97377 10.0379 5.14959C10.2137 5.3254 10.3125 5.56386 10.3125 5.8125V10.5C10.3125 10.7486 10.2137 10.9871 10.0379 11.1629C9.8621 11.3387 9.62364 11.4375 9.375 11.4375H2.625C2.37636 11.4375 2.1379 11.3387 1.96209 11.1629C1.78627 10.9871 1.6875 10.7486 1.6875 10.5V5.8125C1.6875 5.56386 1.78627 5.3254 1.96209 5.14959C2.1379 4.97377 2.37636 4.875 2.625 4.875H4.5"
                stroke="currentColor"
                strokeMiterlimit="10"
                strokeMiterLimit="square"
              />
            </g>
            <defs>
              <clipPath id="clip0_371_37">
                <rect width="12" height="12" fill="currentColor" />
              </clipPath>
            </defs>
          </svg>
        </span>

        <span
          className={`md:text-base px-1.5 text-sm text-center 
          block md:text-inherit text-[#3989DD]`}
        >
          Export Entries
        </span>
      </CSVLink>
    </div>
  );
};

const DrawManagement = ({ rewardTypes }) => {
  const dispatch = useDispatch();
  const drawWinnersFilters = useSelector(drawWinnersFiltersState);
  const drawWinners = useSelector(drawWinnersState);

  const dateRange = drawWinnersFilters?.dateRange;
  const totalPages = drawWinnersFilters?.totalPages;
  const currentPage = drawWinnersFilters?.currentPage;

  const [campaign] = useContext(CampaignContext);
  const [user] = useContext(UserContext);

  const [allDrawWinners, setAllDrawWinners] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const isRunDraw = searchParams.get("select_winners") === "1";

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [page, setPage] = useState(1);
  const [pageNumberLimit, setPageNumberLimit] = useState(3);
  const [maxPageNumberLimit, setMaxPageNumberLimit] = useState(3);
  const [minPageNumberLimit, setMinPageNumberLimit] = useState(0);
  const [pages, setPages] = useState(1);
  const [size, setSize] = useState(20);
  const [dataToExport, setDataToExport] = useState([]);

  const onClose = () => {
    setSuccess(false);
    setOpen(false);
    getAllDrawWinners();
  };

  useEffect(() => {
    if (isRunDraw) {
      return;
    }
    getAllDrawWinners();
  }, [isRunDraw, dateRange, currentPage]);

  const getDataToExport = async ({ limit, endDate, startDate }) => {
    try {
      const {
        data: { data },
      } = await restService.Entries.getDrawWinners(
        campaign.id,
        user.accessToken,
        0,
        limit,
        endDate,
        startDate
      );

      const result = data?.data;

      setDataToExport(result);
    } catch (error) {
      console.log(error);
    }
  };

  const getAllDrawWinners = async (showSpinner = true) => {
    if (showSpinner && !drawWinners.length) {
      setLoading(true);
    }
    try {
      const startDate = new Date(dateRange[0].startDate).toISOString();
      const endDate = new Date(dateRange[0].endDate).toISOString();

      const {
        data: { data },
      } = await restService.Entries.getDrawWinners(
        campaign.id,
        user.accessToken,
        currentPage - 1,
        size,
        endDate,
        startDate
      );

      const result = data?.data;
      const totalPages = data?.totalPages;
      const totalElements = data?.totalElements;

      // setAllDrawWinners(result);
      setPages(totalPages);
      dispatch(setDrawWinners(result));
      dispatch(
        setDrawWinnersFilters({
          ...drawWinnersFilters,
          totalPages,
        })
      );

      if (totalElements && totalElements > 0) {
        getDataToExport({ limit: totalElements, endDate, startDate });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const admins = [
    "5b8995a9172dceb1ecef152b",
    "64023188a1aaf0523d68d359",
    "5c7f8112172dce8b5877f8a0",
    "64d0d633369b7c7de01497d4",
  ];

  return (
    <>
      {loading ? (
        <div className="h-full min-h-[20vh] w-full flex items-center justify-center">
          <PulseLoader color="#FF6A3A" />
        </div>
      ) : (
        <div className="bg-white shadow rounded mt-8 md:px-7 py-5">
          {drawWinners && !isRunDraw ? (
            <>
              <div className="flex md:items-center md:gap-5 md:px-0 px-2 gap-2 justify-between flex-col md:flex-row">
                <div className="flex border rounded md:items-center flex-col md:gap-5 gap-2 md:flex-row">
                  <DatePicker
                    state={dateRange}
                    setState={(date) => {
                      dispatch(
                        setDrawWinnersFilters({
                          ...drawWinnersFilters,
                          dateRange: date,
                        })
                      );
                    }}
                  />
                </div>

                <div className="flex gap-3">
                  <div className="text-[#3989DD] w-full flex-1 md:w-auto   rounded">
                    <ExportData transactions={dataToExport} />
                  </div>
                  {/* <button onClick={() => setIsRunDraw(true)}>
                    Export Entries
                  </button> */}

                  {admins.includes(user?.id) && (
                    <div>
                      <button
                        onClick={() => {
                          setSearchParams({
                            tab: "Entries Management",
                            select_winners: "1",
                          });
                        }}
                        className="text-white w-full md:w-auto flex-1 bg-[#3989DD] border border-[#3989DD] text-xs md:text-base py-[6px] px-4 transition hover:bg-[#235080] hover:border-[#235080] rounded"
                      >
                        Select Winners
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full mt-4">
                <Table
                  admins={admins}
                  rewardTypes={rewardTypes}
                  open={open}
                  setOpen={setOpen}
                  onClose={onClose}
                  success={success}
                  setSuccess={setSuccess}
                  allDrawWinners={drawWinners}
                  setPage={setPage}
                  page={page}
                  pageNumberLimit={pageNumberLimit}
                  setPageNumberLimit={setPageNumberLimit}
                  setMaxPageNumberLimit={setMaxPageNumberLimit}
                  maxPageNumberLimit={maxPageNumberLimit}
                  minPageNumberLimit={minPageNumberLimit}
                  setMinPageNumberLimit={setMinPageNumberLimit}
                  pages={totalPages}
                  setPages={setPages}
                  size={size}
                  setSize={setSize}
                  handleFetchDrawWinners={() => getAllDrawWinners(false)}
                />
              </div>
            </>
          ) : (
            <RunDraw
              setIsRunDraw={setIsRunDraw}
              open={open}
              setOpen={setOpen}
              onClose={onClose}
              success={success}
              setSuccess={setSuccess}
              setLoading={setLoading}
              loading={loading}
            />
          )}
        </div>
      )}
    </>
  );
};

export default DrawManagement;
